import {
  Box,
  Button,
  Checkbox,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useMediaQuery,
  VStack,
} from "@chakra-ui/react";
import { ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onAgree: () => void;
  privacyUrl: string | null;
  termsUrl: string | null;
}

const getTitleResourceKey = (
  privacyUrl: string | null,
  termsUrl: string | null,
): string => {
  if (privacyUrl && !termsUrl) {
    return "Terms.Agreement.title.privacyPolicy";
  } else if (!privacyUrl && termsUrl) {
    return "Terms.Agreement.title.termsOfService";
  }
  return "Terms.Agreement.title";
};

export function TermsAgreementModal({
  isOpen,
  onClose,
  onAgree,
  privacyUrl = null,
  termsUrl = null,
}: Readonly<Props>): ReactElement {
  const [isMobile] = useMediaQuery("(max-width: 768px)");
  const { t } = useTranslation();

  const [agreePrivacy, setAgreePrivacy] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const isAgreed =
    (privacyUrl ? agreePrivacy : true) && (termsUrl ? agreeTerms : true);

  const titleResourceKey = getTitleResourceKey(privacyUrl, termsUrl);
  const desktopModalStyle = isMobile
    ? {}
    : {
        maxH: "90vh",
        h: "90vh",
        maxW: "70vw",
      };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      closeOnOverlayClick={false}
      size={isMobile ? "full" : "xl"}
    >
      <ModalOverlay />
      <ModalContent
        margin={0}
        display="flex"
        flexDirection="column"
        {...desktopModalStyle}
      >
        <ModalHeader>{t(titleResourceKey)}</ModalHeader>
        <ModalBody flex="1" display="flex" overflow="auto">
          <VStack spacing={1} flex="1" align="stretch">
            {termsUrl ? (
              <Box
                as="iframe"
                sandbox="allow-popups allow-popups-to-escape-sandbox"
                src={termsUrl}
                flex={1}
                height="100%"
                borderWidth={1}
                borderStyle="solid"
              />
            ) : null}

            {privacyUrl ? (
              <Box
                as="iframe"
                sandbox="allow-popups allow-popups-to-escape-sandbox"
                src={privacyUrl}
                flex={1}
                height="100%"
                borderWidth={1}
                borderStyle="solid"
              />
            ) : null}

            {termsUrl ? (
              <Checkbox
                isChecked={agreeTerms}
                onChange={(e) => setAgreeTerms(e.target.checked)}
              >
                {t("Terms.Agreement.agreeToTermsOfService")}
              </Checkbox>
            ) : null}

            {privacyUrl ? (
              <Checkbox
                isChecked={agreePrivacy}
                onChange={(e) => setAgreePrivacy(e.target.checked)}
              >
                {t("Terms.Agreement.agreeToPrivacyPolicy")}
              </Checkbox>
            ) : null}
          </VStack>
        </ModalBody>
        <ModalFooter gap={2}>
          <Button variant="ghost" size="sm" onClick={onClose}>
            {t("Terms.Agreement.disagree")}
          </Button>
          <Button
            variant="btn_secondary"
            size="sm"
            onClick={onAgree}
            isDisabled={!isAgreed}
          >
            {t("Terms.Agreement.agree")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
